import { expectedHoursInRange } from "@/features/dashboard/shell/range-utils";

import { MEDDREAM_STALE_MS } from "../constants";
import type {
  IMeddreamHourlyMetric,
  MeddreamPeriodKpis,
} from "../schemas/meddream-hourly";

/**
 * Agrega KPIs del período sobre docs horarios.
 * Avg ponderado = sum(connectionSum)/sum(samplesCount) si hay connectionSum;
 * si no, media de activeConnectionsAvg.
 */
export function aggregateMeddreamKpis(
  metrics: IMeddreamHourlyMetric[],
  startMs: number,
  endMs: number
): MeddreamPeriodKpis {
  const hoursWithData = metrics.length;
  const expectedHours = expectedHoursInRange(startMs, endMs);

  let peakConnections = 0;
  let sessionHourActivity = 0;
  let restartsTotal = 0;
  let connectionSumTotal = 0;
  let samplesTotal = 0;
  let avgSum = 0;

  const sparkPeak: number[] = [];
  const sparkAvg: number[] = [];
  const sparkSessions: number[] = [];
  const sparkRestarts: number[] = [];

  for (const m of metrics) {
    peakConnections = Math.max(peakConnections, m.activeConnectionsMax);
    sessionHourActivity += m.uniqueSessionsCount;
    restartsTotal += m.restartsCount;
    avgSum += m.activeConnectionsAvg;
    if (typeof m.connectionSum === "number") {
      connectionSumTotal += m.connectionSum;
    }
    samplesTotal += m.samplesCount;

    sparkPeak.push(m.activeConnectionsMax);
    sparkAvg.push(m.activeConnectionsAvg);
    sparkSessions.push(m.uniqueSessionsCount);
    sparkRestarts.push(m.restartsCount);
  }

  const avgConnections =
    samplesTotal > 0 && connectionSumTotal > 0
      ? Math.round(connectionSumTotal / samplesTotal)
      : hoursWithData > 0
        ? Math.round(avgSum / hoursWithData)
        : 0;

  return {
    peakConnections,
    avgConnections,
    sessionHourActivity,
    restartsTotal,
    hoursWithData,
    expectedHours,
    coverageRatio: expectedHours > 0 ? hoursWithData / expectedHours : 0,
    sparkPeak,
    sparkAvg,
    sparkSessions,
    sparkRestarts,
  };
}

/** True si updatedAt supera el umbral de stale (default 15 min ≈ 3× cron de 5 min). */
export function isMeddreamDataStale(
  updatedAt: Date | undefined | null,
  nowMs: number = Date.now(),
  staleMs: number = MEDDREAM_STALE_MS
): boolean {
  if (!updatedAt) return true;
  return nowMs - updatedAt.getTime() > staleMs;
}

/**
 * Detecta huecos: horas esperadas en [startMs, endMs) sin documento.
 */
export function findMetricGaps(
  metrics: IMeddreamHourlyMetric[],
  startMs: number,
  endMs: number
): number[] {
  const present = new Set(metrics.map((m) => m.hourStartMs));
  const gaps: number[] = [];
  const hourMs = 60 * 60 * 1000;
  const alignedStart = Math.floor(startMs / hourMs) * hourMs;
  for (let t = alignedStart; t < endMs; t += hourMs) {
    if (!present.has(t)) gaps.push(t);
  }
  return gaps;
}
